import React, { useState, useEffect, useContext, useCallback } from "react";
import Axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { toast } from "react-toastify";
import { API_ROUTE, Routes } from "../../Helpers/Constants";
import AuthenticationContext from "../../Store/Authentication-Context";
import { NavBar } from "../NavBar";
import SuppressErrorPopup from "../MainComponents/SuppressErrorPopup";
import ConfirmSuppressionRemovalModal from "../MainComponents/ConfirmSuppressionRemovalModal";
import {
    suppressionColumns,
    endedSuppressionColumns,
    suppressionGridProps,
} from "../MainComponents/SuppressionColumns";
import * as SuppressionApi from "../../Helpers/SuppressionApi";
import ActingOnBehalfBanner from "../MainComponents/ActingOnBehalfBanner";
import { currentManagedOrg, currentSuppressionPageUrl } from "../../Helpers/SuppressionOrg";
import {
    DURATION_MODE,
    resolveExpiry,
    ENDED_VISIBILITY_DAYS,
} from "../../Helpers/SuppressionDuration";

/**
 * Task 9 — Manage Suppressions: every active suppression across the organization's jobs, with
 * amend and remove, plus D4's recently-ended view.
 *
 * Shares its columns and grid options with the Error Suppression page via
 * MainComponents/SuppressionColumns.js. That sharing is deliberate: the two pages show the same
 * underlying data, and defining columns separately here is how they would drift apart.
 */

const VIEW = { ACTIVE: "active", ENDED: "ended" };

export default function ManageSuppressions() {
    const authContext = useContext(AuthenticationContext);
    const { setAuthContext } = authContext;

    const [view, setView] = useState(VIEW.ACTIVE);
    const [rowData, setRowData] = useState([]);
    const [accessLevel, setAccessLevel] = useState("None");
    const [loading, setLoading] = useState(true);

    const [amendTarget, setAmendTarget] = useState(null);
    const [reinstateTarget, setReinstateTarget] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [confirmBusy, setConfirmBusy] = useState(false);

    const canChange = accessLevel === "Change";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await setAuthContext(new Date());
                // Same resolution the API calls use — see the note on ErrorSuppression.js's
                // session check.
                const { orgId } = currentManagedOrg();
                const config = {
                    headers: {
                        Authorisation: `Bearer ${token}`,
                        CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
                    },
                };
                const response = await Axios.get(
                    `${API_ROUTE}${Routes.VALIDATE_TOKEN}/${orgId}`,
                    config
                );
                if (!cancelled && !JSON.parse(response.data)) window.location.href = "/login";
            } catch (err) {
                if (err?.response?.status === 440) window.location.href = "/TimeoutErrorHandler";
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setAuthContext]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result =
                view === VIEW.ENDED
                    ? await SuppressionApi.getRecentlyEndedSuppressions(setAuthContext, {})
                    : await SuppressionApi.getActiveSuppressions(setAuthContext, {});
            setRowData(result.items || []);
            setAccessLevel(result.accessLevel || "None");
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to load your suppressions.");
            if (message) toast.error(message);
        } finally {
            setLoading(false);
        }
    }, [setAuthContext, view]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    /** Seeds the duration step with the suppression's current expiry. */
    function durationFromExisting(row) {
        if (!row.suppressedUntil) return { mode: DURATION_MODE.INDEFINITE, presetDays: null, customDate: null };
        return {
            mode: DURATION_MODE.CUSTOM,
            presetDays: null,
            customDate: new Date(row.suppressedUntil),
        };
    }

    async function confirmAmend({ duration, note }) {
        try {
            await SuppressionApi.amendSuppression(setAuthContext, amendTarget.id, {
                suppressedUntil: resolveExpiry(duration),
                note,
            });
            toast.success(`Suppression for ${amendTarget.errorId} updated.`);
            setAmendTarget(null);
            loadData();
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to update the suppression.");
            if (message) toast.error(message);
        }
    }

    /**
     * D4 — put an ended suppression back with a freshly chosen duration.
     *
     * The old expiry is deliberately NOT reused: it is in the past by definition, so seeding the
     * duration step with it (as durationFromExisting does for amend) would produce a form that
     * validateCustomDate rejects and the customer could never submit.
     */
    async function confirmReinstate({ duration, note }) {
        try {
            await SuppressionApi.reinstateSuppression(setAuthContext, reinstateTarget.id, {
                suppressedUntil: resolveExpiry(duration),
                note,
            });
            toast.success(
                `${reinstateTarget.errorId} suppressed again on "${reinstateTarget.jobName}".`
            );
            setReinstateTarget(null);
            loadData();
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to re-enable the suppression.");
            if (message) toast.error(message);
        }
    }

    async function runConfirmedRemoval() {
        setConfirmBusy(true);
        try {
            await confirm.action();
            toast.success("Suppression removed.");
            setConfirm(null);
            loadData();
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to remove the suppression.");
            if (message) toast.error(message);
        } finally {
            setConfirmBusy(false);
        }
    }

    const columnDefs =
        view === VIEW.ENDED
            ? endedSuppressionColumns({
                  canChange,
                  onReinstate: (row) => setReinstateTarget(row),
              })
            : suppressionColumns({
                  canChange,
                  onAmend: (row) => setAmendTarget(row),
                  onRemove: (row) =>
                      setConfirm({
                          title: "Remove suppression",
                          message: `Remove the suppression for ${row.errorId} on "${row.jobName}"? You will start being notified about this error on that job again.`,
                          action: () => SuppressionApi.removeSuppression(setAuthContext, row.id),
                      }),
              });

    return (
        <div>
            <NavBar page="manageSuppressions" authContext={authContext} />
            <div style={{ padding: 20 }}>
                <ActingOnBehalfBanner />
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Manage Suppressions</h5>
                    {/* btn-primary to match the "Add Tenant" button on MasterAdmin
                        (MasterAdmin.js:4197) — see the note on ErrorSuppression.js's
                        counterpart, including why the organisation travels with the link. */}
                    <a className="btn btn-primary" href={currentSuppressionPageUrl("/ErrorSuppression")}>
                        Back to Error Suppression
                    </a>
                </div>

                {/*
                  D4 — two views. "Recently ended" rather than "Recently expired" because N8 (settled
                  2026-08-17) confirmed the view carries BOTH expired and user-removed suppressions,
                  each re-enableable. Deactivation-cascade deletions are excluded — see
                  SuppressionApi.getRecentlyEndedSuppressions.
                */}
                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link${view === VIEW.ACTIVE ? " active" : ""}`}
                            onClick={() => setView(VIEW.ACTIVE)}
                        >
                            Active
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link${view === VIEW.ENDED ? " active" : ""}`}
                            onClick={() => setView(VIEW.ENDED)}
                        >
                            Recently ended
                        </button>
                    </li>
                </ul>

                <p className="text-muted" style={{ maxWidth: 820 }}>
                    {view === VIEW.ENDED
                        ? `Suppressions that expired or were removed in the last ${ENDED_VISIBILITY_DAYS} days. Re-enable one to start suppressing that error again; after ${ENDED_VISIBILITY_DAYS} days they are removed from this list.`
                        : "Every known error currently suppressed on one of your backup jobs. Removing or shortening a suppression only affects backup sessions processed from that point onwards."}
                </p>

                {loading ? (
                    <div className="alert alert-light border text-center py-5 mb-0">Loading…</div>
                ) : rowData.length === 0 ? (
                    <div className="alert alert-light border text-center py-5 mb-0">
                        {view === VIEW.ENDED ? (
                            <>
                                <p className="mb-1 fw-semibold">
                                    Nothing has ended in the last {ENDED_VISIBILITY_DAYS} days.
                                </p>
                                <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                                    Suppressions appear here when they expire or you remove them.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="mb-1 fw-semibold">You have no active suppressions.</p>
                                <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                                    Suppress a known error from the{" "}
                                    <a href={currentSuppressionPageUrl("/ErrorSuppression")}>
                                        Error Suppression
                                    </a>{" "}
                                    page to see it here.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="ag-theme-alpine">
                        <AgGridReact {...suppressionGridProps} rowData={rowData} columnDefs={columnDefs} />
                    </div>
                )}
            </div>

            <SuppressErrorPopup
                show={!!amendTarget}
                knownError={
                    amendTarget
                        ? { errorId: amendTarget.errorId, name: amendTarget.knownErrorName }
                        : null
                }
                jobs={[]}
                jobStepDisabled
                initialJobIds={amendTarget ? [amendTarget.jobId] : []}
                initialDuration={amendTarget ? durationFromExisting(amendTarget) : null}
                // C5 — carry the existing justification in, so changing an expiry cannot erase it.
                initialNote={amendTarget ? amendTarget.note : null}
                onConfirm={confirmAmend}
                onClose={() => setAmendTarget(null)}
            />

            {/*
              D4 — re-enabling. initialDuration is left undefined on purpose so the popup falls back
              to its own valid default: the ended suppression's expiry is in the past, and seeding it
              here would give the customer a form they cannot submit.
            */}
            <SuppressErrorPopup
                show={!!reinstateTarget}
                knownError={
                    reinstateTarget
                        ? {
                              errorId: reinstateTarget.errorId,
                              name: reinstateTarget.knownErrorName,
                          }
                        : null
                }
                jobs={[]}
                jobStepDisabled
                initialJobIds={reinstateTarget ? [reinstateTarget.jobId] : []}
                onConfirm={confirmReinstate}
                onClose={() => setReinstateTarget(null)}
            />

            <ConfirmSuppressionRemovalModal
                show={!!confirm}
                title={confirm?.title}
                message={confirm?.message}
                busy={confirmBusy}
                onConfirm={runConfirmedRemoval}
                onClose={() => setConfirm(null)}
            />
        </div>
    );
}
